// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons-studio.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// icons-studio.ts — STUDIO's ICON SET, and the only DUAL-OPTICAL set in the
// programme (R21).
//
// THE SHARED ID CONTRACT (same as every set). A style does not get its own id
// space: it answers the SAME symbol ids the Blueprint sprite defines, so every
// call site in the app is unchanged and a style switch is a sprite swap.
// scripts/check-icon-refs.mjs enforces both halves with measured counts:
//   1. NO ORPHANS — an id only this set defines would render for STUDIO and
//      blank for everyone else.
//   2. COMPLETE COVERAGE — all 127 REQUIRED ids, drawn by this set. The old
//      rule ("a set need not cover every id; a Blueprint fallback is a named,
//      counted state") was REVERSED by the owner on 2026-09-07 after finding
//      mixed glyph languages in production: a styled set ships every id in its
//      own visual language or `icons:check` is RED. A fallback survives only
//      as a build-time diagnostic. STUDIO carried 103 of them; this file now
//      draws all 103, and STUDIO's COVERAGE_DEBT row is gone.
//
// THE DRAWING RULES (spec §6). Lucide-derived geometry on the 24 grid, 1.5px
// stroke, round caps and joins, monochrome via currentColor, and NO FILLS —
// styles.css sets `.icon { fill: none }` under this style, and a CSS
// declaration outranks a `fill="currentColor"` presentation attribute, so a
// filled shape here would silently render as a hole. Stroke weight is not set
// per-glyph: one number in styles.css governs the whole set.
//
// ------------------------------------------------------------- DUAL OPTICAL
//
// The distinguishing craft move, and the only one in the programme: the 20px
// DEFAULT drawing and the 16px DENSE-TABLE drawing are DIFFERENT DRAWINGS.
// The dense cut drops interior detail and re-spaces strokes so it does not go
// muddy at table scale, rather than being the default scaled down. Nobody
// notices this and everybody feels it, which is the whole style.
//
// HOW THE SELECTION WORKS, and why it is not a class. Both drawings ship
// inside ONE symbol, wrapped in two `<g>`s whose `display` reads an INHERITED
// custom property. A `<use>` renders its symbol into a SHADOW TREE: a
// document selector cannot reach inside it, and `display` is not an inherited
// property — so `.icon .dense { display: … }` would match nothing and the
// dense cut would silently never appear. Custom properties DO inherit across
// that boundary, which makes them the only channel available. styles.css sets
// `--ico-o20 / --ico-o16` at the STUDIO root (default drawing) and flips them
// on the dense per-datum surfaces; the fallbacks baked into the `var()` calls
// below mean a symbol rendered with neither property set still draws exactly
// one glyph rather than two or none.
//
// WHAT SHIPS TWO DRAWINGS, and what ships one. The 15 dual-optical ids are
// the ones that actually render inside a dense grid — the whole status ladder,
// the two intent marks, and the row affordances. Everything else ships ONE
// drawing at chrome scale. That split is deliberate and did NOT widen when the
// set was completed: `styles.css` flips `--ico-o20/--ico-o16` only on the six
// per-datum selectors perf-budget already calls dense, and a single-drawing
// symbol carries no `<g>` switch, so it renders identically on both sides of
// that flip. Adding a second cut to a glyph that never appears in a table
// would be craft nobody can see, which is the opposite of this style.
//
// THE COMPLETION (2026-09-07). The 103 ids below were on Blueprint's Lucide
// artwork until the owner's ruling. They are re-drawn, not copied: STUDIO's
// geometry is rounder and more openly spaced than Lucide's (plates at r=9 and
// the rounded-square plate at rx=5.6, arcs quantised to whole degrees, detail
// dropped wherever it would fuse at 16px), and several ids that Blueprint
// draws with the SAME artwork are separated here because the app uses them
// for different things: `book` is a closed volume and `book-open` a spread;
// `scroll` is a rolled sheet and `scroll-text` a written page; `gate` is a
// gateway and `clipboard-check` a clipboard. Where Blueprint gives a legacy
// nautical id an unrelated glyph, the CALL SITE decides: `tack` is the
// "refresh usage meters" button and the revisions/baselines section mark, so
// it is drawn as a revision arrow around a hub — not as a sailing manoeuvre.
//
// INTENT SILHOUETTES (R0.4). Each intent carries a distinct silhouette rather
// than relying on colour: neutral is a plain ring, additive is a `+`,
// destructive is a trash can, and ERROR IS A TRIANGLE-BANG — which is why
// `circle-alert` is re-cut here as a triangle. The id names the role; the
// style draws it.

/** Wrap the two optical cuts of one glyph into a single symbol body. */
function dual(o20: string, o16: string): string {
  return (
    `<g style="display:var(--ico-o20,inline)">${o20}</g>` +
    `<g style="display:var(--ico-o16,none)">${o16}</g>`
  );
}

/** THE LEVEL — STUDIO's brand mark, and the artwork behind both `helm` and
 * `ship-wheel`. Two horizontal bars of unequal length inside a rounded square
 * at the md radius: signal levels, a fader pair, an equals sign tipped into
 * motion. Two unequal bars is STUDIO's silhouette in the programme (R22) and
 * is distinct at 16px from circle / hexagon / squared roundel /
 * cross-with-a-dot / solid block / half-disc. The FRAME stays `currentColor`
 * and only the bars would take the primary, which puts the whole style
 * contract — one shared ground, only the primary varies — into the mark. */
const LEVEL =
  '<rect x="2.6" y="2.6" width="18.8" height="18.8" rx="5.6" />' +
  '<path d="M6.8 10h10.4" stroke-width="2.2" />' +
  '<path d="M6.8 15h5.8" stroke-width="2.2" />';

/**
 * id -> symbol INNER markup (the `<symbol>` wrapper and the viewBox are added
 * by icons.ts, so a set cannot get either wrong).
 */
export const STUDIO_SYMBOLS: Record<string, string> = {
  // ---- the brand level, at chrome scale only --------------------------
  helm: LEVEL,
  'ship-wheel': LEVEL,

  // ---- the mode pair: a plate exposed, and a plate covered ------------
  sun:
    '<circle cx="12" cy="12" r="4.6" /> <path d="M12 2.4v2.6" /> <path d="M12 19v2.6" />' +
    ' <path d="M2.4 12h2.6" /> <path d="M19 12h2.6" /> <path d="m5.2 5.2 1.9 1.9" />' +
    ' <path d="m16.9 16.9 1.9 1.9" /> <path d="m18.8 5.2-1.9 1.9" /> <path d="m7.1 16.9-1.9 1.9" />',
  moon: '<path d="M20.4 14.6A8.8 8.8 0 0 1 9.4 3.6a8.8 8.8 0 1 0 11 11Z" />',

  // ---- the four instrument glyphs -------------------------------------
  search: '<circle cx="10.8" cy="10.8" r="6.8" /> <path d="M15.7 15.7 20.8 20.8" />',
  gauge:
    '<path d="M3.8 18.2a10 10 0 1 1 16.4 0" /> <path d="M12 14.4 16.4 9.2" />' +
    ' <path d="M4.6 12.6h1.8" /> <path d="M12 5.2v1.8" /> <path d="M19.4 12.6h-1.8" />',
  activity: '<path d="M3 12h4l2.6-6.4 4.8 12.8L17 12h4" />',
  pencil: '<path d="m4 20 1-4L16.6 4.4a2 2 0 0 1 2.8 0l.2.2a2 2 0 0 1 0 2.8L8 19l-4 1Z" /> <path d="m15 6 3 3" />',
  eye:
    '<path d="M2.6 12a10.2 10.2 0 0 1 18.8 0 10.2 10.2 0 0 1-18.8 0Z" />' +
    ' <circle cx="12" cy="12" r="3.2" />',

  // ---- the status ladder: seven states, two optical cuts each ---------
  // Default: a 9-radius plate with its interior mark. Dense: the plate drops
  // to 8 and every interior tick that would fuse with it at 16px is gone.
  'st-ring': dual(
    '<circle cx="12" cy="12" r="9" /> <circle cx="12" cy="12" r="4.4" />',
    '<circle cx="12" cy="12" r="8" />',
  ),
  'st-half': dual(
    '<circle cx="12" cy="12" r="9" /> <path d="M12 3v18" /> <path d="M15.4 7.4h1.8" />' +
      ' <path d="M16.6 11.2h1.6" /> <path d="M15.4 16.6h1.8" />',
    '<circle cx="12" cy="12" r="8" /> <path d="M12 4v16" />',
  ),
  'st-check': dual(
    '<circle cx="12" cy="12" r="9" /> <path d="m8.2 12.3 2.6 2.6 4.9-5.2" />',
    '<path d="m3.6 12.4 4.8 4.8L20.4 5.6" stroke-width="2" />',
  ),
  'st-cross': dual(
    '<circle cx="12" cy="12" r="9" /> <path d="m15.2 8.8-6.4 6.4" /> <path d="m8.8 8.8 6.4 6.4" />',
    '<path d="m19 5-14 14" stroke-width="2" /> <path d="m5 5 14 14" stroke-width="2" />',
  ),
  'st-pause': dual(
    '<circle cx="12" cy="12" r="9" /> <path d="M10 8.8v6.4" /> <path d="M14 8.8v6.4" />',
    '<path d="M8.6 4.4v15.2" stroke-width="2.4" /> <path d="M15.4 4.4v15.2" stroke-width="2.4" />',
  ),
  'st-dash': dual(
    '<path d="M10.2 3.1a9 9 0 0 1 3.6 0" /> <path d="M13.8 20.9a9 9 0 0 1-3.6 0" />' +
      ' <path d="M17.4 4.6a9 9 0 0 1 2 2" /> <path d="M3.1 13.8a9 9 0 0 1 0-3.6" />' +
      ' <path d="M19.4 17.4a9 9 0 0 1-2 2" /> <path d="M20.9 10.2a9 9 0 0 1 0 3.6" />' +
      ' <path d="M4.6 6.6a9 9 0 0 1 2-2" /> <path d="M6.6 19.4a9 9 0 0 1-2-2" />',
    '<path d="M8.6 3.4a8 8 0 0 1 6.8 0" /> <path d="M15.4 20.6a8 8 0 0 1-6.8 0" />' +
      ' <path d="M3.4 15.4a8 8 0 0 1 0-6.8" /> <path d="M20.6 8.6a8 8 0 0 1 0 6.8" />',
  ),
  'st-seal': dual(
    '<circle cx="12" cy="12" r="9" /> <circle cx="12" cy="12" r="6" />' +
      ' <path d="m9.4 12.2 2 2 3.4-3.6" />',
    '<circle cx="12" cy="12" r="8" /> <path d="m8.2 12.3 2.6 2.6 4.9-5.2" />',
  ),

  // ---- the two intent marks, each with its own silhouette (R0.4) ------
  // ERROR is a triangle-bang, not a circle: the silhouette carries the intent
  // so the mark still reads with colour removed entirely.
  'circle-alert': dual(
    '<path d="M12 3.4 21.6 19.8H2.4Z" /> <path d="M12 9.6v4.4" /> <path d="M12 17h.01" />',
    '<path d="M12 2.6 22.4 20.6H1.6Z" stroke-width="1.8" /> <path d="M12 9.4v4.8" stroke-width="1.8" />' +
      ' <path d="M12 17.6h.01" stroke-width="1.8" />',
  ),
  'circle-check': dual(
    '<circle cx="12" cy="12" r="9" /> <path d="m8.4 12.4 2.6 2.6 5-5.4" />',
    '<path d="m3.6 12.4 4.8 4.8L20.4 5.6" stroke-width="2" />',
  ),

  // ---- the row affordances --------------------------------------------
  'chevron-down': dual(
    '<path d="m6.4 9.4 5.6 5.6 5.6-5.6" />',
    '<path d="m4.8 8.8 7.2 7.2 7.2-7.2" stroke-width="2" />',
  ),
  ellipsis: dual(
    '<path d="M5.4 12h.01" /> <path d="M12 12h.01" /> <path d="M18.6 12h.01" />',
    '<path d="M3.6 12h.01" stroke-width="2.6" /> <path d="M12 12h.01" stroke-width="2.6" />' +
      ' <path d="M20.4 12h.01" stroke-width="2.6" />',
  ),
  terminal: dual(
    '<rect x="2.4" y="3.6" width="19.2" height="16.8" rx="2.4" /> <path d="m6.4 9.2 3 2.8-3 2.8" />' +
      ' <path d="M12.4 15.2h5" />',
    '<path d="m3.2 6.4 6 5.6-6 5.6" stroke-width="2" /> <path d="M12.8 17.6h8" stroke-width="2" />',
  ),
  x: dual(
    '<path d="m17.6 6.4-11.2 11.2" /> <path d="m6.4 6.4 11.2 11.2" />',
    '<path d="m19.4 4.6-14.8 14.8" stroke-width="2" /> <path d="m4.6 4.6 14.8 14.8" stroke-width="2" />',
  ),
  'trash-2': dual(
    '<path d="M3.6 6.4h16.8" /> <path d="M8.4 6.4V4.6a1.4 1.4 0 0 1 1.4-1.4h4.4a1.4 1.4 0 0 1 1.4 1.4v1.8" />' +
      ' <path d="M5.8 6.4 7 19.6a1.6 1.6 0 0 0 1.6 1.4h6.8a1.6 1.6 0 0 0 1.6-1.4L18.2 6.4" />' +
      ' <path d="M10.4 10.6v6" /> <path d="M13.6 10.6v6" />',
    '<path d="M2.6 6.6h18.8" stroke-width="2" />' +
      ' <path d="M4.8 6.6 6.2 20.4a1.6 1.6 0 0 0 1.6 1.4h8.4a1.6 1.6 0 0 0 1.6-1.4L19.2 6.6" stroke-width="1.8" />' +
      ' <path d="M8.6 6.6V4.4h6.8v2.2" stroke-width="1.8" />',
  ),
  plus: dual(
    '<path d="M12 5.6v12.8" /> <path d="M5.6 12h12.8" />',
    '<path d="M12 3.6v16.8" stroke-width="2.2" /> <path d="M3.6 12h16.8" stroke-width="2.2" />',
  ),

  // ====================================================================
  // THE COMPLETION — the 103 ids that were still on Blueprint artwork
  // until the owner's 2026-09-07 ruling. One drawing each, chrome scale.
  // ====================================================================

  // ---- the nautical level: the framework's own vocabulary -------------
  // These ids predate Lucide in this app and several of them are the marks
  // the corpus reader sees most (`knot` = dependencies, `sonar` = open
  // questions, `buoy`/`telescope`/`flag` = section widgets), so they get
  // real drawings rather than a nearest-Lucide substitute.
  lighthouse:
    '<path d="M9 21.4 10.3 10.2h3.4L15 21.4Z" /> <path d="M7.2 21.4h9.6" />' +
    ' <rect x="8.8" y="5.6" width="6.4" height="4.6" rx="1.2" /> <path d="M12 2.6v3" />' +
    ' <path d="m4 5.6 3.4 1.6" /> <path d="m4 10.4 3.4-1.6" />' +
    ' <path d="m20 5.6-3.4 1.6" /> <path d="m20 10.4-3.4-1.6" /> <path d="M9.9 14.6h4.2" />',
  compass: '<circle cx="12" cy="12" r="9" /> <path d="m15.6 8.4-2.3 4.9-4.9 2.3 2.3-4.9Z" />',
  anchor:
    '<circle cx="12" cy="4.6" r="2.2" /> <path d="M12 6.8v14.6" /> <path d="M8.6 10.2h6.8" />' +
    ' <path d="M4.2 12.8a7.8 7.8 0 0 0 15.6 0" /> <path d="M4.2 12.8h2.4" /> <path d="M19.8 12.8h-2.4" />',
  ship:
    '<path d="M3.4 14.6h17.2l-2.2 4.6a2 2 0 0 1-1.8 1.2H7.4a2 2 0 0 1-1.8-1.2Z" />' +
    ' <path d="M12 14.6V3.4" /> <path d="M12 5.2h5.6L12 9.8" />',
  cargo:
    '<rect x="3.6" y="6.6" width="16.8" height="12.8" rx="1.6" /> <path d="M3.6 11h16.8" />' +
    ' <path d="M9.2 11v8.4" /> <path d="M14.8 11v8.4" /> <path d="M7.4 6.6V4.2h9.2v2.4" />',
  porthole:
    '<circle cx="12" cy="12" r="9" /> <circle cx="12" cy="12" r="5" /> <path d="M12 3v2" />' +
    ' <path d="M12 19v2" /> <path d="M3 12h2" /> <path d="M19 12h2" />',
  knot:
    '<path d="M2.8 8.4h7a3.6 3.6 0 0 1 0 7.2h-7" /> <path d="M21.2 8.4h-7a3.6 3.6 0 0 0 0 7.2h7" />',
  logbook:
    '<path d="M5.4 4.8a2.2 2.2 0 0 1 2.2-2.2h11v18.8h-11a2.2 2.2 0 0 1-2.2-2.2Z" />' +
    ' <path d="M5.4 17.2h13.2" /> <path d="M10.2 2.6v7.6l2.2-1.6 2.2 1.6V2.6" />',
  horizon:
    '<path d="M2.6 18.8h18.8" /> <path d="M6.6 15a5.4 5.4 0 0 1 10.8 0" /> <path d="M12 3.2v2.6" />' +
    ' <path d="m4.8 6.4 1.8 1.8" /> <path d="m19.2 6.4-1.8 1.8" /> <path d="M6.6 21.4h10.8" />',
  flag:
    '<path d="M5.4 21.4V3.4" /> <path d="M5.4 4.6h12.6a1 1 0 0 1 .82 1.58L16.6 9.2l2.22 3.02a1 1 0 0 1-.82 1.58H5.4Z" />',
  buoy:
    '<circle cx="12" cy="14.6" r="6.8" /> <path d="M12 7.8V4.4" /> <circle cx="12" cy="3" r="1.4" />' +
    ' <path d="M5.7 12.4h12.6" /> <path d="M5.8 17h12.4" />',
  telescope:
    '<path d="m4.4 14.2 12.4-7.4a1.4 1.4 0 0 1 1.92.5l1.5 2.6a1.4 1.4 0 0 1-.52 1.92L7.3 19.2a1.4 1.4 0 0 1-1.92-.5l-1.5-2.6a1.4 1.4 0 0 1 .52-1.9Z" />' +
    ' <path d="m8.4 18.4-1.6 3.2" /> <path d="m12.6 16 2.6 5.6" />',
  crane:
    '<path d="M7.6 21.4V4.2" /> <path d="M4.4 21.4h6.4" /> <path d="M7.6 4.2h11" />' +
    ' <path d="m7.6 8.8 8-4.4" /> <path d="M16.6 4.2v4.6" /> <circle cx="16.6" cy="10.6" r="1.8" />',
  gate:
    '<path d="M4.2 21.4V5.6" /> <path d="M19.8 21.4V5.6" /> <path d="M4.2 5.6h15.6" />' +
    ' <path d="M4.2 9.6h15.6" /> <path d="M8.4 9.6v11.8" /> <path d="M12 9.6v11.8" /> <path d="M15.6 9.6v11.8" />',
  crew:
    '<circle cx="9" cy="7.6" r="3.4" /> <path d="M3.2 20.4a5.8 5.8 0 0 1 11.6 0" />' +
    ' <path d="M16.2 4.8a3.4 3.4 0 0 1 0 6.6" /> <path d="M17 14.9a5.8 5.8 0 0 1 3.8 5.5" />',
  wave:
    '<path d="M2.6 8.6q2.35 2 4.7 0t4.7 0 4.7 0 4.7 0" /> <path d="M2.6 14.2q2.35 2 4.7 0t4.7 0 4.7 0 4.7 0" />' +
    ' <path d="M2.6 19.8q2.35 2 4.7 0t4.7 0 4.7 0 4.7 0" />',
  signal:
    '<path d="M4.6 20.4v-3.6" /> <path d="M9.6 20.4v-7.6" /> <path d="M14.6 20.4v-11.6" /> <path d="M19.4 20.4V4.6" />',
  sonar:
    '<circle cx="12" cy="12" r="1.9" /> <path d="M15.8 8.2a5.4 5.4 0 0 1 0 7.6" />' +
    ' <path d="M18.6 5.4a9.4 9.4 0 0 1 0 13.2" /> <path d="m10.7 10.7-5.3-5.3" />',
  tack:
    '<path d="M19.8 12a7.8 7.8 0 1 1-2.28-5.52" /> <path d="M19.8 4.6v4.4h-4.4" /> <circle cx="12" cy="12" r="2.2" />',
  dividers:
    '<circle cx="12" cy="4.2" r="1.8" /> <path d="m11.2 6.2-5.6 14.4" /> <path d="m12.8 6.2 5.6 14.4" />' +
    ' <path d="M8.1 15.4a8 8 0 0 0 7.8 0" />',

  // ---- the nav rail: the 19 rows the owner counted --------------------
  // Nine of the nineteen are here (the rest are above or in the original
  // cut). SQUARE-TERMINAL, GRID-2X2, PANELS-TOP-LEFT and SQUARE all sit on
  // STUDIO's plate — the rounded square at rx=5.6 that the brand LEVEL is
  // drawn inside — so the chrome reads as one family at a glance.
  'square-terminal':
    '<rect x="2.6" y="2.6" width="18.8" height="18.8" rx="5.6" /> <path d="m7.4 9.4 2.8 2.6-2.8 2.6" />' +
    ' <path d="M12.6 15h4.4" />',
  library:
    '<rect x="3.4" y="4.6" width="4.2" height="14.4" rx="1.2" /> <rect x="9" y="4.6" width="4.2" height="14.4" rx="1.2" />' +
    ' <rect x="14.6" y="7" width="4.2" height="12" rx="1.2" /> <path d="M2.6 21.4h18.8" />',
  'notebook-tabs':
    '<rect x="4.6" y="2.6" width="14.8" height="18.8" rx="2.2" /> <path d="M2.6 7h2" /> <path d="M2.6 12h2" />' +
    ' <path d="M2.6 17h2" /> <path d="M14.6 2.6v18.8" /> <path d="M14.6 7.6h4.8" /> <path d="M14.6 12h4.8" />' +
    ' <path d="M14.6 16.4h4.8" />',
  'git-pull-request':
    '<circle cx="6.2" cy="6.2" r="2.8" /> <circle cx="17.8" cy="17.8" r="2.8" /> <path d="M6.2 9v11.6" />' +
    ' <path d="M12.6 6.2h3a2.2 2.2 0 0 1 2.2 2.2V15" />',
  'badge-check':
    '<path d="M8.6 3.8h6.8l4.8 4.8v6.8l-4.8 4.8H8.6l-4.8-4.8V8.6Z" /> <path d="m9 12.2 2.2 2.2 4.2-4.6" />',
  siren:
    '<path d="M7.4 17.4v-4.8a4.6 4.6 0 0 1 9.2 0v4.8" /> <rect x="5.2" y="17.4" width="13.6" height="3.8" rx="1.4" />' +
    ' <path d="M12 4.2V6" /> <path d="m4.8 8.2 1.6.8" /> <path d="m19.2 8.2-1.6.8" /> <path d="M2.8 14h1.8" />' +
    ' <path d="M21.2 14h-1.8" />',
  antenna:
    '<path d="M12 21.4v-9.2" /> <circle cx="12" cy="10.4" r="1.8" /> <path d="M8.4 6.8a5 5 0 0 0 0 7.2" />' +
    ' <path d="M15.6 6.8a5 5 0 0 1 0 7.2" /> <path d="M5.8 4.2a8.6 8.6 0 0 0 0 12.4" />' +
    ' <path d="M18.2 4.2a8.6 8.6 0 0 1 0 12.4" />',
  container:
    '<rect x="2.6" y="6.4" width="18.8" height="11.2" rx="1.6" /> <path d="M6.6 6.4v11.2" />' +
    ' <path d="M10.4 6.4v11.2" /> <path d="M14.2 6.4v11.2" /> <path d="M18 6.4v11.2" />',
  workflow:
    '<rect x="3" y="3" width="7.6" height="7.6" rx="2.2" /> <rect x="13.4" y="13.4" width="7.6" height="7.6" rx="2.2" />' +
    ' <path d="M6.8 10.6v4.4a2.2 2.2 0 0 0 2.2 2.2h4.4" />',
  chart: '<path d="M3.6 3.6v15.4a1.4 1.4 0 0 0 1.4 1.4h15.4" /> <path d="m7.4 15.6 3.4-3.8 2.8 2.4 4.4-5.4" />',
  'grid-2x2': '<rect x="3" y="3" width="18" height="18" rx="4.4" /> <path d="M12 3.4v17.2" /> <path d="M3.4 12h17.2" />',
  house:
    '<path d="M3.6 10.2a2 2 0 0 1 .72-1.54l6.4-5.4a2 2 0 0 1 2.56 0l6.4 5.4a2 2 0 0 1 .72 1.54v8.8a2.2 2.2 0 0 1-2.2 2.2H5.8a2.2 2.2 0 0 1-2.2-2.2Z" />' +
    ' <path d="M9.4 21.2v-6.4a1 1 0 0 1 1-1h3.2a1 1 0 0 1 1 1v6.4" />',
  'panels-top-left':
    '<rect x="3" y="3" width="18" height="18" rx="4.4" /> <path d="M3.4 9.2h17.2" /> <path d="M9.2 9.2v11.4" />',

  // ---- the two rail-foot instruments the owner saw as placeholders ----
  // GEAR is a ring, a hub and six teeth. It is NOT the sun with a hole in
  // it: the mode glyph is one small disc inside eight long rays, this is two
  // concentric rings inside six short ones — the pair had to separate at
  // 16px in the same rail, so the tooth count and the ring count differ.
  'circle-help':
    '<circle cx="12" cy="12" r="9" /> <path d="M9.6 9.6a2.5 2.5 0 0 1 4.9.6c0 1.7-2.5 2.2-2.5 3.9" />' +
    ' <path d="M12 17.2h.01" />',
  gear:
    '<circle cx="12" cy="12" r="6.4" /> <circle cx="12" cy="12" r="2.4" /> <path d="M12 5.6V3.4" />' +
    ' <path d="M12 18.4v2.2" /> <path d="m17.5 8.8 1.95-1.1" /> <path d="m6.5 8.8-1.95-1.1" />' +
    ' <path d="m6.5 15.2-1.95 1.1" /> <path d="m17.5 15.2 1.95 1.1" />',

  // ---- the corpus object types: epic, feature, story, defect, … -------
  layers:
    '<path d="m12 3.4 8.6 4.2-8.6 4.2-8.6-4.2Z" /> <path d="m3.4 12.4 8.6 4.2 8.6-4.2" />' +
    ' <path d="m3.4 16.8 8.6 4.2 8.6-4.2" />',
  box: '<path d="M12 3.2 20.4 7.6v8.8L12 20.8 3.6 16.4V7.6Z" /> <path d="M3.6 7.6 12 12l8.4-4.4" /> <path d="M12 12v8.8" />',
  'scroll-text':
    '<path d="M6.4 2.6h8.6l4.6 4.6v12.2a2 2 0 0 1-2 2H6.4a2 2 0 0 1-2-2V4.6a2 2 0 0 1 2-2Z" />' +
    ' <path d="M15 2.6v4.6h4.6" /> <path d="M8 8.6h3.4" /> <path d="M8 12.2h8" /> <path d="M8 15.8h8" />',
  scroll:
    '<path d="M8 3.4h9a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H7.4A2.4 2.4 0 0 1 5 17.9V5.8A2.4 2.4 0 0 1 7.4 3.4Z" />' +
    ' <path d="M5 5.8a2.4 2.4 0 0 0 2.4 2.4H10" /> <path d="M11.6 12.4h4.6" /> <path d="M11.6 16h4.6" />',
  bug:
    '<rect x="7.4" y="7.6" width="9.2" height="11.2" rx="4.6" /> <path d="M9.2 7.6a2.8 2.8 0 0 1 5.6 0" />' +
    ' <path d="M7.4 12H3.6" /> <path d="M20.4 12h-3.8" /> <path d="m6 6.4 2 1.6" /> <path d="m18 6.4-2 1.6" />' +
    ' <path d="m6 18.4 2-1.6" /> <path d="m18 18.4-2-1.6" /> <path d="M12 11.2v5.6" />',
  'clipboard-check':
    '<rect x="8.4" y="2.6" width="7.2" height="3.8" rx="1.2" />' +
    ' <path d="M16 4.4h1.8a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6.2a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2H8" />' +
    ' <path d="m9 13.4 2.2 2.2 4.2-4.4" />',
  'clipboard-list':
    '<rect x="8.4" y="2.6" width="7.2" height="3.8" rx="1.2" />' +
    ' <path d="M16 4.4h1.8a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6.2a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2H8" />' +
    ' <path d="M12.2 11.4h4" /> <path d="M12.2 16h4" /> <path d="M8.2 11.4h.01" /> <path d="M8.2 16h.01" />',
  book:
    '<path d="M6.4 2.6h10a2 2 0 0 1 2 2v14.8a2 2 0 0 0-2 2h-10A1.4 1.4 0 0 1 5 20V4a1.4 1.4 0 0 1 1.4-1.4Z" />' +
    ' <path d="M5 17.6h13.4" /> <path d="M8.8 2.6v15" />',
  'book-open':
    '<path d="M12 7.4v13" />' +
    ' <path d="M2.6 5a1 1 0 0 1 1-1h4.6A3.8 3.8 0 0 1 12 7.4 3.8 3.8 0 0 1 15.8 4h4.6a1 1 0 0 1 1 1v12.4a1 1 0 0 1-1 1h-5a3 3 0 0 0-3 2.2 3 3 0 0 0-3-2.2h-5a1 1 0 0 1-1-1Z" />',
  map:
    '<path d="m3.6 6.2 5.4-2.4 6 2.8 5.4-2.4v14.6l-5.4 2.4-6-2.8-5.4 2.4Z" /> <path d="M9 3.8v14.6" />' +
    ' <path d="M15 6.6v14.6" />',
  microscope:
    '<path d="M4.4 21.4h15.2" /> <path d="M7.6 18.2h8.8" /> <path d="M13.6 18.2a6.4 6.4 0 0 0 0-12.8" />' +
    ' <path d="M9.8 13.4h2.8" /> <path d="M9.4 11.4a1.8 1.8 0 0 1-1.8-1.8V6.2h5.2v3.4a1.8 1.8 0 0 1-1.8 1.8Z" />' +
    ' <path d="M12 6.2V3.6a1 1 0 0 0-1-1H9.6a1 1 0 0 0-1 1v2.6" />',
  hammer:
    '<path d="M4 5.8h11.4a3.4 3.4 0 0 1 3.4 3.4v1.6H4Z" />' +
    ' <path d="M9 10.8v8.4a2.2 2.2 0 0 0 4.4 0v-8.4" />',
  'flask-conical':
    '<path d="M9.6 2.6v6.2a2 2 0 0 1-.24 1.02L4.44 19a1.8 1.8 0 0 0 1.56 2.7h12a1.8 1.8 0 0 0 1.56-2.7l-4.92-9.18a2 2 0 0 1-.24-1.02V2.6" />' +
    ' <path d="M8.2 2.6h7.6" /> <path d="M7 15.4h10" />',
  stethoscope:
    '<path d="M5.4 2.6v2" /> <path d="M10.6 2.6v2" />' +
    ' <path d="M4.4 4.6H4a1.6 1.6 0 0 0-1.6 1.6v3.4a5.6 5.6 0 0 0 11.2 0V6.2A1.6 1.6 0 0 0 12 4.6h-.4" />' +
    ' <path d="M8 15.2v1.2a5 5 0 0 0 10 0v-2.2" /> <circle cx="18" cy="11.6" r="2.2" />',
  scale:
    '<path d="M12 3.4v17.6" /> <path d="M7.4 21h9.2" /> <path d="M4.4 7.2h15.2" />' +
    ' <path d="M1.8 13.6 4.4 7.2 7 13.6a3.6 3.6 0 0 1-5.2 0Z" />' +
    ' <path d="m17 13.6 2.6-6.4 2.6 6.4a3.6 3.6 0 0 1-5.2 0Z" />',
  wrench:
    '<path d="M15.6 6.4a1 1 0 0 0 0 1.42l1.58 1.58a1 1 0 0 0 1.42 0l3-3a6 6 0 0 1-7.92 7.12l-7.58 7.58a1.9 1.9 0 0 1-2.7-2.7l7.58-7.58A6 6 0 0 1 18.6 3.4Z" />',
  server:
    '<rect x="2.6" y="3" width="18.8" height="7.4" rx="2" /> <rect x="2.6" y="13.6" width="18.8" height="7.4" rx="2" />' +
    ' <path d="M6.6 6.7h.01" /> <path d="M6.6 17.3h.01" /> <path d="M10.4 6.7h3.4" /> <path d="M10.4 17.3h3.4" />',
  database:
    '<ellipse cx="12" cy="5.4" rx="8.4" ry="2.8" />' +
    ' <path d="M3.6 5.4v13.2c0 1.55 3.76 2.8 8.4 2.8s8.4-1.25 8.4-2.8V5.4" />' +
    ' <path d="M3.6 12c0 1.55 3.76 2.8 8.4 2.8s8.4-1.25 8.4-2.8" />',
  'git-merge':
    '<circle cx="6.2" cy="6.2" r="2.8" /> <circle cx="17.8" cy="17.8" r="2.8" />' +
    ' <path d="M6.2 20.6V9a8.8 8.8 0 0 0 8.8 8.8" />',
  'git-branch':
    '<circle cx="17.8" cy="6.2" r="2.8" /> <circle cx="6.2" cy="17.8" r="2.8" /> <path d="M6.2 15V3.4" />' +
    ' <path d="M15 6.2a8.8 8.8 0 0 0-8.8 8.8" />',
  shield:
    '<path d="M12 21.2c-4.4-1.6-7-4.2-7-8.4V6.2a1 1 0 0 1 1-1c2.2 0 4.6-1.2 5.4-2a.9.9 0 0 1 1.2 0c.8.8 3.2 2 5.4 2a1 1 0 0 1 1 1v6.6c0 4.2-2.6 6.8-7 8.4Z" />',
  puzzle:
    '<path d="M4.4 4.4h5.4v1a2.2 2.2 0 1 0 4.4 0v-1h5.4v5.4h-1a2.2 2.2 0 1 0 0 4.4h1v5.4h-5.4v-1a2.2 2.2 0 1 0-4.4 0v1H4.4Z" />',
  hourglass:
    '<path d="M6.2 2.6h11.6" /> <path d="M6.2 21.4h11.6" />' +
    ' <path d="M7.4 2.6v3.9a2 2 0 0 0 .6 1.42L12 12l-4 4.08a2 2 0 0 0-.6 1.42v3.9" />' +
    ' <path d="M16.6 2.6v3.9a2 2 0 0 1-.6 1.42L12 12l4 4.08a2 2 0 0 1 .6 1.42v3.9" />',

  // ---- the assistant spark and the pointer/status marks ---------------
  assistant:
    '<path d="M12 3.2c.9 4.2 1.6 4.9 5.8 5.8-4.2.9-4.9 1.6-5.8 5.8-.9-4.2-1.6-4.9-5.8-5.8 4.2-.9 4.9-1.6 5.8-5.8Z" />' +
    ' <path d="M18 16.2c.4 1.7.7 2 2.4 2.4-1.7.4-2 .7-2.4 2.4-.4-1.7-.7-2-2.4-2.4 1.7-.4 2-.7 2.4-2.4Z" />',
  bell:
    '<path d="M5.6 17.4h12.8a.8.8 0 0 0 .6-1.32C17.9 14.9 17 13.6 17 10.4a5 5 0 0 0-10 0c0 3.2-.9 4.5-2 5.68a.8.8 0 0 0 .6 1.32Z" />' +
    ' <path d="M10.2 20.4a2 2 0 0 0 3.6 0" />',
  star: '<path d="M12 3.2 14.3 8.9 20.5 9.3 15.7 13.2 17.3 19.3 12 15.9 6.7 19.3 8.3 13.2 3.5 9.3 9.7 8.9Z" />',
  bookmark: '<path d="M6.6 3.4h10.8a1.4 1.4 0 0 1 1.4 1.4v15.6l-6.8-3.8-6.8 3.8V4.8a1.4 1.4 0 0 1 1.4-1.4Z" />',
  clock: '<circle cx="12" cy="12" r="9" /> <path d="M12 6.6V12l3.8 2.2" />',
  zap: '<path d="M13.4 2.6 4.6 13.4h6.4l-.4 8 8.8-10.8h-6.4Z" />',
  'circle-dot': '<circle cx="12" cy="12" r="9" /> <circle cx="12" cy="12" r="2" />',
  'circle-x': '<circle cx="12" cy="12" r="9" /> <path d="m15.6 8.4-7.2 7.2" /> <path d="m8.4 8.4 7.2 7.2" />',
  ban: '<circle cx="12" cy="12" r="9" /> <path d="m5.64 5.64 12.72 12.72" />',
  check: '<path d="m19.4 6.6-10.2 10.4-4.6-4.6" />',
  square: '<rect x="3" y="3" width="18" height="18" rx="4.4" />',

  // ---- the chevron/arrow family: one construction, eight bearings -----
  // Every chevron is the SAME 5.6-unit leg pair, rotated. Blueprint's are a
  // 6-unit leg; the shorter leg is what lets the pair sit inside STUDIO's
  // tighter row rhythm without the arms reaching the cell edge.
  chev: '<path d="m9.4 6.4 5.6 5.6-5.6 5.6" />',
  'chevron-up': '<path d="m6.4 14.6 5.6-5.6 5.6 5.6" />',
  'chevron-left': '<path d="m14.6 17.6-5.6-5.6 5.6-5.6" />',
  'chevrons-right': '<path d="m6.8 7 5 5-5 5" /> <path d="m13.4 7 5 5-5 5" />',
  'arrow-left': '<path d="M20.4 12H4.6" /> <path d="m11 18.4-6.4-6.4L11 5.6" />',
  'arrow-right': '<path d="M3.6 12h15.8" /> <path d="m13 5.6 6.4 6.4-6.4 6.4" />',
  'arrow-up-right': '<path d="M7.4 16.6 16.6 7.4" /> <path d="M9 7.4h7.6V15" />',
  'circle-arrow-up':
    '<circle cx="12" cy="12" r="9" /> <path d="M12 16.4V7.6" /> <path d="m8.4 11.2 3.6-3.6 3.6 3.6" />',
  play: '<path d="M8.6 5.2 19.4 12 8.6 18.8Z" />',
  send: '<path d="m21.4 2.6-8.6 18.8-3-8.2-8.2-3Z" /> <path d="M21.4 2.6 9.8 13.2" />',

  // ---- the circular arrows: three ids, three silhouettes --------------
  // ROTATE-CW is one arc with one head, REFRESH-CW two arcs with two, and
  // TACK (the usage-meter refresh and the revisions mark) an arc around a
  // hub. Drawn as one family they were indistinguishable at 16px, which is
  // the mistake the Mainframe cut measured and re-cut ten glyphs for.
  'rotate-cw':
    '<path d="M20.4 12a8.4 8.4 0 1 1-8.4-8.4c2.35 0 4.6.94 6.28 2.6l2.12 2" /> <path d="M20.4 3.8v4.8h-4.8" />',
  'refresh-cw':
    '<path d="M3.6 12a8.4 8.4 0 0 1 14.32-5.94l2.48 2.34" /> <path d="M20.4 3.6v4.8h-4.8" />' +
    ' <path d="M20.4 12a8.4 8.4 0 0 1-14.32 5.94L3.6 15.6" /> <path d="M3.6 20.4v-4.8h4.8" />',
  history:
    '<path d="M3.6 12a8.4 8.4 0 1 0 2.46-5.94L3.6 8.4" /> <path d="M3.6 3.6v4.8h4.8" />' +
    ' <path d="M12 7.6V12l3.4 1.8" />',
  'list-restart':
    '<path d="M3.4 5.4h17.2" /> <path d="M3.4 12h4.6" /> <path d="M3.4 18.6h4.6" />' +
    ' <path d="M20.6 15.4a5 5 0 1 1-1.46-3.54" /> <path d="M19.2 8v3.9h-3.9" />',
  'undo-2': '<path d="M9.4 14.6 4 9.2l5.4-5.4" /> <path d="M4 9.2h10.4a5.8 5.8 0 0 1 0 11.6H10.6" />',
  reply: '<path d="M20.6 18.6v-2.2a4.4 4.4 0 0 0-4.4-4.4H4.2" /> <path d="m9 17-5-5 5-5" />',

  // ---- the transfer pair, and the frame verbs -------------------------
  download:
    '<path d="M12 3.4v11.6" /> <path d="m7.2 10.2 4.8 4.8 4.8-4.8" />' +
    ' <path d="M3.6 15.6v3.2a2 2 0 0 0 2 2h12.8a2 2 0 0 0 2-2v-3.2" />',
  upload:
    '<path d="M12 15V3.4" /> <path d="m7.2 8.2 4.8-4.8 4.8 4.8" />' +
    ' <path d="M3.6 15.6v3.2a2 2 0 0 0 2 2h12.8a2 2 0 0 0 2-2v-3.2" />',
  'external-link':
    '<path d="M14.6 3.4h6v6" /> <path d="M20.6 3.4 11 13" />' +
    ' <path d="M18.4 13.8v5a2 2 0 0 1-2 2H5.6a2 2 0 0 1-2-2V7.6a2 2 0 0 1 2-2h5" />',
  'maximize-2':
    '<path d="M14.6 3.4h6v6" /> <path d="m20.6 3.4-6.4 6.4" /> <path d="m3.4 20.6 6.4-6.4" />' +
    ' <path d="M9.4 20.6h-6v-6" />',
  'minimize-2':
    '<path d="m13.8 10.2 6.8-6.8" /> <path d="M19.8 10.2h-6v-6" /> <path d="m3.4 20.6 6.8-6.8" />' +
    ' <path d="M4.2 13.8h6v6" />',
  'log-in':
    '<path d="m10.4 16.6 4.6-4.6-4.6-4.6" /> <path d="M15 12H3.6" />' +
    ' <path d="M14.6 3.4h4.2a2 2 0 0 1 2 2v13.2a2 2 0 0 1-2 2h-4.2" />',
  'log-out':
    '<path d="m16.4 16.6 4.6-4.6-4.6-4.6" /> <path d="M21 12H9.6" />' +
    ' <path d="M9.4 20.6H5.2a2 2 0 0 1-2-2V5.4a2 2 0 0 1 2-2h4.2" />',
  link:
    '<path d="M10.2 13.4a4.8 4.8 0 0 0 7.24.52l2.6-2.6a4.8 4.8 0 0 0-6.79-6.79l-1.5 1.49" />' +
    ' <path d="M13.8 10.6a4.8 4.8 0 0 0-7.24-.52l-2.6 2.6a4.8 4.8 0 0 0 6.79 6.79l1.49-1.49" />',

  // ---- documents, mail, capture and the row tools ---------------------
  'folder-open':
    '<path d="M2.8 9.8V5.6a1.8 1.8 0 0 1 1.8-1.8h4a1.6 1.6 0 0 1 1.3.66l1.02 1.4a1.6 1.6 0 0 0 1.3.66h4.4a1.8 1.8 0 0 1 1.8 1.8v1.48" />' +
    ' <path d="M4.6 20.2a1.8 1.8 0 0 1-1.8-1.8V9.8h18.2a1.4 1.4 0 0 1 1.36 1.75l-1.7 7a1.8 1.8 0 0 1-1.76 1.65Z" />',
  'file-pen':
    '<path d="M13.2 21.4H18a2 2 0 0 0 2-2V8.6L14.4 3H6.4a2 2 0 0 0-2 2v8.6" />' +
    ' <path d="M14 3v4.6a1 1 0 0 0 1 1h4.6" /> <path d="m10.4 13.6 3 3-4.8 4.8-3.6.6.6-3.6Z" />',
  'square-pen':
    '<path d="M12.4 3.4H5.6a2 2 0 0 0-2 2v12.8a2 2 0 0 0 2 2h12.8a2 2 0 0 0 2-2v-6.8" />' +
    ' <path d="m17.4 3.2 3.4 3.4-7.6 7.6-4.2.8.8-4.2Z" />',
  copy:
    '<rect x="8.4" y="8.4" width="12.6" height="12.6" rx="2.6" />' +
    ' <path d="M4.6 15.6A1.6 1.6 0 0 1 3 14V4.6A1.6 1.6 0 0 1 4.6 3H14a1.6 1.6 0 0 1 1.6 1.6" />',
  pin:
    '<path d="M12 16.8v4.6" />' +
    ' <path d="M9.8 3v5.8a2 2 0 0 1-.9 1.66l-2 1.34a2 2 0 0 0-.9 1.66v.94a1.6 1.6 0 0 0 1.6 1.6h8.8a1.6 1.6 0 0 0 1.6-1.6v-.94a2 2 0 0 0-.9-1.66l-2-1.34a2 2 0 0 1-.9-1.66V3" />' +
    ' <path d="M8.6 3h6.8" />',
  filter: '<path d="M3.4 4.4h17.2l-6.6 7.8v6.4l-4-2v-4.4Z" />',
  mail:
    '<rect x="2.6" y="4.6" width="18.8" height="14.8" rx="2.4" />' +
    ' <path d="m3.2 7.4 7.76 4.94a2 2 0 0 0 2.08 0L20.8 7.4" />',
  'message-square':
    '<path d="M3.4 5.4a2 2 0 0 1 2-2h13.2a2 2 0 0 1 2 2v9.6a2 2 0 0 1-2 2H8.6l-4.2 3.6a.6.6 0 0 1-1-.46Z" />',
  camera:
    '<path d="M4.4 7.6h2.4a1.6 1.6 0 0 0 1.4-.84l.9-1.66a1.6 1.6 0 0 1 1.4-.9h3.8a1.6 1.6 0 0 1 1.4.9l.9 1.66a1.6 1.6 0 0 0 1.4.84h2a2 2 0 0 1 2 2v8.4a2 2 0 0 1-2 2H4.4a2 2 0 0 1-2-2V9.6a2 2 0 0 1 2-2Z" />' +
    ' <circle cx="12" cy="13.6" r="3.4" />',
  'user-plus':
    '<circle cx="9.4" cy="7.6" r="3.6" /> <path d="M3.2 20.4a6.2 6.2 0 0 1 12.4 0" /> <path d="M19 8.6v5.2" />' +
    ' <path d="M21.6 11.2h-5.2" />',

  // ── release-scope SECTION TYPES (#810 PR 4): one per renderer family,
  // cut in this set's own construction so the corpus TOC's section rows are
  // own-set under every style (icons:check refuses a fallback).
  'rs-prose':
    '<rect x="4.6" y="3" width="14.8" height="18" rx="3.2" /> <path d="M8.4 8.2h7.2" />' +
    ' <path d="M8.4 11.8h7.2" /> <path d="M8.4 15.4h4.8" />',
  'rs-paired-lists':
    '<rect x="3" y="4" width="8" height="16" rx="2.6" />' +
    ' <rect x="13" y="4" width="8" height="16" rx="2.6" /> <path d="M5.6 9h2.8" />' +
    ' <path d="M5.6 12.6h2.8" /> <path d="M15.6 9h2.8" /> <path d="M15.6 12.6h2.8" />',
  'rs-table':
    '<rect x="3" y="4" width="18" height="16" rx="3.4" /> <path d="M3.4 9.4h17.2" />' +
    ' <path d="M12 9.4v10.2" /> <path d="M3.4 14.6h17.2" />',
  'rs-table-expansion':
    '<rect x="3" y="3.4" width="18" height="9.2" rx="3" /> <path d="M3.4 8.2h17.2" />' +
    ' <rect x="5.6" y="15.2" width="12.8" height="5.4" rx="2" />' +
    ' <path d="m10.4 10.2 1.6 1.6 1.6-1.6" />',
  'rs-rules':
    '<circle cx="6" cy="6.8" r="1.4" /> <circle cx="6" cy="12" r="1.4" />' +
    ' <circle cx="6" cy="17.2" r="1.4" /> <path d="M10.4 6.8h9.6" /> <path d="M10.4 12h9.6" />' +
    ' <path d="M10.4 17.2h6.4" />',
  'rs-state-machine':
    '<rect x="2.6" y="8.4" width="7.2" height="7.2" rx="2.2" />' +
    ' <rect x="14.2" y="8.4" width="7.2" height="7.2" rx="2.2" /> <path d="M9.8 12h4.4" />' +
    ' <path d="m12.6 10.4 1.6 1.6-1.6 1.6" />',
  'rs-spec-cards':
    '<rect x="5.4" y="8.4" width="15.6" height="12.4" rx="3" />' +
    ' <path d="M3 15.2V6a3 3 0 0 1 3-3h9.2" /> <path d="M8.8 12.6h8.4" /> <path d="M8.8 16.2h5.2" />',
  'rs-wireframe':
    '<rect x="3" y="4" width="18" height="16" rx="3.4" /> <path d="M3.4 8.6h17.2" />' +
    ' <rect x="6.4" y="11.6" width="6.2" height="4.8" rx="1.6" /> <path d="M15.2 12.4h2.8" />' +
    ' <path d="M15.2 15.4h2.8" />',
};

/** Every id this set re-cuts. Sorted so the gate's output is stable. */
export const STUDIO_ICON_IDS: string[] = Object.keys(STUDIO_SYMBOLS).sort();

/** The ids that ship TWO optical drawings rather than one. Counted by the
 * icons row in the smoke — "the set has a dense cut" is a number, not a
 * claim, and a symbol that lost one of its two `<g>` wrappers would drop out
 * of this list rather than silently render the wrong drawing. */
export const STUDIO_DUAL_OPTICAL_IDS: string[] = Object.entries(STUDIO_SYMBOLS)
  .filter(([, art]) => art.includes('--ico-o20') && art.includes('--ico-o16'))
  .map(([id]) => id)
  .sort();
